import React, { FC, RefCallback } from 'react';
import classNames from 'classnames';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { Alignment } from '@app/shared/enums/alignment.enum';
import {
  amountRules,
  shortDateViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { useLocale } from '@app/core/hooks/useLocale';
import { isYearGreyedOutInFocusMode } from './utils/irrHurdleCalcHelper';

export interface TrancheSectionRowProps {
  years: string[];
  valueKey: keyof Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto;
  isBold?: boolean;
  isShaded?: boolean;
  isNumber?: boolean;
  label: string;
  isDate?: boolean;
  exitDates: Record<
    string,
    Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto[]
  >;
  navigatedSelectedYear: Optional<string>;
  lastRowRef?: RefCallback<HTMLTableRowElement>;
}

const PARENT_CLASSNAME = 'irr-hurdle-calculations-table';

const TrancheSectionRow: FC<TrancheSectionRowProps> = ({
  years,
  label,
  valueKey,
  exitDates,
  navigatedSelectedYear,
  isBold = false,
  isShaded = false,
  isDate = false,
  isNumber = false,
  lastRowRef = undefined,
}) => {
  const { l } = useLocale();

  return (
    <tr ref={lastRowRef}>
      <th
        className={classNames(
          'table-primary__cell--label',
          styles[`${PARENT_CLASSNAME}__cell--label`],
          {
            [styles[`${PARENT_CLASSNAME}__cell--bold`]]: isBold,
            [styles[`${PARENT_CLASSNAME}__cell--shaded`]]: isShaded,
          }
        )}>
        {label}
      </th>
      {years.map((year, yearIdx) => {
        const tranches = exitDates[year] || [];

        return (
          <React.Fragment key={year}>
            {tranches.map((tranche, trancheIdx) => {
              if (tranche[valueKey] === null) {
                return <td key={trancheIdx} />;
              }
              if (
                (label === l('_EventDate') || label === l('_EventName')) &&
                !tranche['eventNarrative']
              ) {
                return <td key={trancheIdx} />;
              }
              return (
                <td
                  key={trancheIdx}
                  className={classNames(
                    'table-primary__cell--value',
                    styles[`${PARENT_CLASSNAME}__cell--value`],
                    {
                      [styles[`${PARENT_CLASSNAME}__cell--bold`]]: isBold,
                      [styles[`${PARENT_CLASSNAME}__cell--shaded`]]: isShaded,
                      [styles['greyed-out']]: isYearGreyedOutInFocusMode(
                        year,
                        navigatedSelectedYear
                      ),
                    }
                  )}>
                  <CellValue
                    value={tranche[valueKey]}
                    alignment={Alignment.Right}
                    {...(isNumber ? amountRules : isDate ? { ...shortDateViewRules } : {})}
                  />
                </td>
              );
            })}
            {yearIdx < years.length - 1 && (
              <td
                className={classNames({
                  [styles[`${PARENT_CLASSNAME}__cell--shaded`]]: isShaded,
                  [styles['greyed-out']]: isYearGreyedOutInFocusMode(year, navigatedSelectedYear),
                })}
              />
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
};

export default TrancheSectionRow;
